import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { Layout as ReactGridLayout, Layouts as ReactGridLayouts } from 'react-grid-layout';

import { colors } from '@dexma/ui-components';

import { dispatchDashboardEdit } from 'adapter/in/dashboards/context/EditDashboard';
import Layout, { LayoutType, WidgetLayout } from 'domain/entity/layout';
import { GridLayouts, GridLayoutPerWidget } from 'adapter/in/dashboards/components/generic/ReactGrid';
import { BREAKPOINTS, CELL_MARGIN, RATIO, COLUMNS, Breakpoint } from 'adapter/in/dashboards/components/Grid';
import { useSettingsView } from 'adapter/in/dashboards/context/Settings/view';
import { useWidgetsView } from 'adapter/in/dashboards/context/Widgets/view';
import { ConstraintsPerLayout } from 'domain/entity/settings';
import i18n from 'asset/translation';
import { widgetLayoutFitsConstraints } from 'adapter/in/dashboards/containers/Grid';
import { ALERT_ACTION_TYPE, dispatchAlertToaster, useAlertToaster } from 'adapter/in/common/context/Alert';
import { useNavigation } from 'adapter/in/dashboards/hooks/useNavigation';

export const areGridLayoutsEqual = (layoutA: ReactGridLayout[], layoutB: ReactGridLayout[]): boolean => {
    const layoutBMap = new Map(layoutB.map((widgetLayout) => [widgetLayout.i, widgetLayout]));

    const isLayoutAaLayoutBSubset = layoutA.every((widgetLayoutA) => {
        const widgetLayoutB = layoutBMap.get(widgetLayoutA.i);

        if (widgetLayoutB) {
            if (
                widgetLayoutB.i === widgetLayoutA.i &&
                widgetLayoutB.w === widgetLayoutA.w &&
                widgetLayoutB.h === widgetLayoutA.h &&
                widgetLayoutB.x === widgetLayoutA.x &&
                widgetLayoutB.y === widgetLayoutA.y
            ) {
                return true;
            }
            return false;
        }
        return false;
    });

    return isLayoutAaLayoutBSubset && layoutA.length === layoutB.length;
};

export const useGridView = (
    gridLayouts: GridLayouts,
    allGridLayouts: GridLayouts,
    editingLayout: boolean,
    selectedLayout: LayoutType,
    isEditEnabled: boolean,
    isDesktop: boolean,
    isTablet: boolean,
    setAllGridLayouts: React.Dispatch<React.SetStateAction<GridLayouts>>,
    wrongSizeWidgets: Record<LayoutType, Record<string, ConstraintsPerLayout>>
): {
    cellSize: {
        width: number;
        height: number;
    };
    backgroundStyle: CSSProperties;
    backgroundColor: string;
    currentBreakpoint: Breakpoint;
    breakpoints: { [P: string]: number };
    containerWidth: number;
    editLayoutWidthStyle: string;
    onWidthChange: (newWidth: number) => void;
    onDragStart: () => void;
    onDragStop: () => void;
    onResizeStop: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onResize: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onBreakpointChange: (newBreakpoint: Breakpoint) => void;
    onLayoutChange: (currentLayout: ReactGridLayout[], allLayouts: ReactGridLayouts) => void;
    widgetBeingResized: undefined | Record<string, ConstraintsPerLayout | null>;
} => {
    const defaultLayoutType = (): LayoutType => {
        if (isTablet) {
            return LayoutType.TABLET;
        }
        if (isDesktop) {
            return LayoutType.DESKTOP;
        }
        return LayoutType.MOBILE;
    };
    const { currentUrl } = useNavigation();
    const [breakpoint, setBreakpoint] = useState<Breakpoint>(defaultLayoutType());
    const [breakpoints, setBreakpoints] = useState<{ [P: string]: number }>(buildBreakpoint(defaultLayoutType()));
    const [widgetBeingResized, setWidgetBeingResized] = useState<undefined | Record<string, ConstraintsPerLayout | null>>(undefined);
    const [background, setBackground] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);
    const originalGridLayouts = gridLayouts;
    const { settings } = useSettingsView();
    const { widgets } = useWidgetsView();

    const [wrongSizeLayouts, setWrongSizeLayouts] = useState<LayoutType[]>([]);
    const { setHasLayoutChanges, setLayouts } = dispatchDashboardEdit();
    const { setWarningAlert, setSuccessAlert } = dispatchAlertToaster();
    const { show: showAlert, type: alertType } = useAlertToaster();
    const cellSize = useMemo(() => calculateCellSize(containerWidth, breakpoint), [containerWidth, breakpoint]);

    const backgroundStyle = useMemo(
        () => generateGridBackground(background, cellSize, [CELL_MARGIN, CELL_MARGIN], COLUMNS[breakpoint], containerWidth),
        [background, containerWidth, breakpoint]
    );

    const style = useMemo(
        () => ({
            background: backgroundStyle,
        }),
        [backgroundStyle]
    );

    const calculateBreakpointDesktop = (): LayoutType => {
        if (containerWidth >= BREAKPOINTS.DESKTOP) {
            return LayoutType.DESKTOP;
        }
        if (containerWidth >= BREAKPOINTS.TABLET) {
            return LayoutType.TABLET;
        }
        return LayoutType.MOBILE;
    };

    const handleLayoutChange = (_: ReactGridLayout[], allLayouts: GridLayouts) => {
        if (editingLayout) {
            setAllGridLayouts(() => ({ ...allGridLayouts, [selectedLayout]: allLayouts[selectedLayout] }));
        }
        if (allLayouts[selectedLayout] && allLayouts[selectedLayout].length > 0) {
            setLayouts?.call(this, new Map([[selectedLayout, asLayout(allLayouts[selectedLayout], selectedLayout)]]));
            setHasLayoutChanges?.call(
                this,
                originalGridLayouts[selectedLayout]
                    ? !areGridLayoutsEqual(allLayouts[selectedLayout], originalGridLayouts[selectedLayout])
                    : true
            );
        }
    };

    const editLayoutWidthStyle =
        editingLayout && breakpoints && Object.keys(breakpoints).length === 1 && Object.keys(breakpoints)[0] !== LayoutType.DESKTOP
            ? `${breakpoints[Object.keys(breakpoints)[0]]}px`
            : '100%';

    useEffect(() => {
        // First time, when the container width is going from 0 to the actual size
        if (containerWidth > 0) {
            let currentBreakpoint = LayoutType.MOBILE;
            if (isEditEnabled) {
                currentBreakpoint = selectedLayout;
                setBreakpoints(buildBreakpoint(selectedLayout));
            } else if (isTablet) {
                currentBreakpoint = LayoutType.TABLET;
            } else if (isDesktop) {
                currentBreakpoint = calculateBreakpointDesktop();
                setBreakpoints(BREAKPOINTS);
            }

            setBreakpoint(currentBreakpoint);
        }
    }, [containerWidth, isEditEnabled, selectedLayout]);

    useEffect(() => {
        if (!widgetBeingResized) {
            Object.keys(wrongSizeWidgets).forEach((layoutType) => {
                if (
                    Object.entries(wrongSizeWidgets[layoutType as LayoutType]).length > 0 &&
                    !wrongSizeLayouts.includes(layoutType as LayoutType)
                ) {
                    setWrongSizeLayouts((prevState) => [...prevState, layoutType as LayoutType]);
                } else if (
                    Object.entries(wrongSizeWidgets[layoutType as LayoutType]).length === 0 &&
                    wrongSizeLayouts.includes(layoutType as LayoutType)
                ) {
                    const wrongSizeLayoutsCopy = [...wrongSizeLayouts];
                    const index = wrongSizeLayoutsCopy.findIndex((wrongLayoutCopy) => wrongLayoutCopy === layoutType);
                    wrongSizeLayoutsCopy.splice(index, 1);
                    setWrongSizeLayouts(wrongSizeLayoutsCopy);
                }
            });
        }
    }, [wrongSizeWidgets, widgetBeingResized]);

    useEffect(() => {
        if (currentUrl.includes('/edit')) {
            if (
                showAlert &&
                wrongSizeLayouts.length === 0 &&
                Object.keys(allGridLayouts).length > 0 &&
                alertType === ALERT_ACTION_TYPE.WARNING
            ) {
                setSuccessAlert(i18n.t('dashboards.edit.dashBoardSizeIssuesSolved'));
            }
            if (wrongSizeLayouts.length > 0) {
                setWarningAlert(
                    i18n.t('dashboards.edit.dashboardHasSizeIssues', {
                        layoutTypes: wrongSizeLayouts
                            .map((layoutType: LayoutType) => i18n.t(`dashboards.layouts.${layoutType.toLowerCase()}SaveFeedback`))
                            .toString()
                            .toLowerCase()
                            .replaceAll(',', ', '),
                    })
                );
            }
        }
    }, [currentUrl, wrongSizeLayouts, alertType, showAlert, allGridLayouts]);

    const onResize = (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => {
        setBackground(true);
        const resizedWidgetType = widgets.filter((widget) => widget.id === newItem.i)[0].type;
        const constraints = settings.widgetTypes
            .filter((setting) => setting.type === resizedWidgetType)[0]
            .constraints.layouts.find((constraintLayout) => constraintLayout.type === breakpoint)!;
        const checkItFits = widgetLayoutFitsConstraints(newItem, constraints);
        if (checkItFits) {
            setWidgetBeingResized({ [newItem.i]: null });
        } else {
            setWidgetBeingResized({ [newItem.i]: constraints });
        }
    };

    const onResizeStop = (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => {
        // Synchro issues with layout if we dont mutate layout directly
        /*eslint-disable */
        setBackground(false);
        if (widgetBeingResized && widgetBeingResized[newItem.i] !== null) {
            const index = layout.findIndex((gridLayout) => gridLayout.i === newItem.i);
            layout[index] = oldItem;
        }
        setWidgetBeingResized(undefined);

        /* eslint-enable */
    };

    return {
        cellSize,
        backgroundStyle: background ? style : {},
        backgroundColor: editingLayout ? colors.gray200 : colors.gray100,
        currentBreakpoint: breakpoint,
        breakpoints,
        containerWidth,
        editLayoutWidthStyle,
        onDragStart: () => setBackground(true),
        onDragStop: () => setBackground(false),
        onResize,
        onResizeStop,
        onWidthChange: setContainerWidth,
        onBreakpointChange: setBreakpoint,
        onLayoutChange: handleLayoutChange,
        widgetBeingResized,
    };
};

const buildBreakpoint = (breakpoint: Breakpoint) => ({ [breakpoint]: BREAKPOINTS[breakpoint] });

const calculateCellSize = (containerWidth: number, breakpoint: LayoutType): { width: number; height: number } => {
    if (containerWidth > 0) {
        const marginSlotsCount = COLUMNS[breakpoint] - 1;
        const totalHorizontalMargin = marginSlotsCount * CELL_MARGIN;
        const freeSpace = containerWidth - totalHorizontalMargin;
        const width = freeSpace / COLUMNS[breakpoint];
        return {
            width,
            height: Math.round(width / RATIO),
        };
    }

    return {
        width: 0,
        height: 0,
    };
};

const generateGridBackground = (
    background: boolean,
    cellSize: { width: number; height: number },
    margin: [number, number],
    columns: number,
    containerWidth: number
): string => {
    if (background) {
        const XMLNS = 'http://www.w3.org/2000/svg';
        const [horizontalMargin, verticalMargin] = margin;
        const rowHeight = cellSize.height + verticalMargin;
        const cellStrokeColor = colors.gray100;
        const cellFillColor = colors.gray200;

        const padding = 1;

        const y = padding;
        const w = cellSize.width - padding * 2;
        const h = cellSize.height - padding * 2;

        const rectangles = Array.from({ length: columns }, (_: string, k: number) => {
            const x = k * (cellSize.width + horizontalMargin) + padding;
            return `<rect stroke='${cellStrokeColor}' stroke-width='2' rx='8' fill='${cellFillColor}' x='${x}' y='${y}' width='${w}' height='${h}'/>`;
        });

        const svg = [`<svg xmlns='${XMLNS}' width='${containerWidth}' height='${rowHeight}'>`, ...rectangles, `</svg>`].join('');

        return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
    }
    return '';
};

const asLayout = (gridLayout: GridLayoutPerWidget[], layoutType: LayoutType): Layout =>
    new Layout(
        layoutType,
        gridLayout.map((gridElement) => asWidgetLayout(gridElement))
    );

const asWidgetLayout = (gridElement: GridLayoutPerWidget): WidgetLayout => ({
    id: gridElement.i,
    position: {
        x: gridElement.x,
        y: gridElement.y,
    },
    size: {
        width: gridElement.w,
        height: gridElement.h,
    },
});
